import type {
  AccessCatalogItemPublic,
  AccessLevelPublic,
  AccessNodePublic,
  AccessRecordPublic,
  AccessRecordStatus,
  UserPublic,
} from '@accounts/shared';

/**
 * The Matrix's shaping, kept pure so both views draw from one answer to "who has what".
 *
 * These are the OUTSIDE-SYSTEM columns, filed by the hierarchy. The suite apps are their
 * own column group, one per registered application, drawn from grants and pending asks
 * (`../appStatus.ts`) rather than from records.
 */

/** Level id → the node picked in that level's filter ('' = every entry). */
export type LevelFilters = Readonly<Record<string, string>>;

/**
 * The toolbar's Show control (FINAL demo `mx-only`): every login, or only what needs work —
 * people with an open record, their active cells stepped back.
 */
export type MatrixShow = 'all' | 'attention';

export const MATRIX_SHOW_OPTIONS: readonly MatrixShow[] = ['all', 'attention'];

export function parseMatrixShow(value: string): MatrixShow {
  return value === 'attention' ? 'attention' : 'all';
}

export interface MatrixFilters {
  levels: LevelFilters;
  people: 'active' | 'all';
  search: string;
  show: MatrixShow;
}

/** Every Matrix cell, suite-app cells included, carries this: the demo's hover fill. */
export const MATRIX_CELL_CLASS = 'mx-cell';

/**
 * The Matrix's dialogs. A cell never opens the add form directly: a filled cell opens its
 * record, an empty one opens "No access recorded", and only that dialog's Add access…
 * moves on to the form (FINAL demo `mxCellModal` → `addAccess`).
 */
export type MatrixDialog =
  | { kind: 'record'; record: AccessRecordPublic }
  | { kind: 'empty'; userId: string; catalogItemId: string }
  | { kind: 'add'; userId: string; catalogItemId: string };

export function dialogForCell(
  userId: string,
  catalogItemId: string,
  record: AccessRecordPublic | undefined,
): MatrixDialog {
  return record === undefined
    ? { kind: 'empty', userId, catalogItemId }
    : { kind: 'record', record };
}

/** Add access… on the empty-cell dialog: the form opens on the same person and row. */
export function addFromEmpty(dialog: Extract<MatrixDialog, { kind: 'empty' }>): MatrixDialog {
  return { kind: 'add', userId: dialog.userId, catalogItemId: dialog.catalogItemId };
}

export function outsideRows(items: readonly AccessCatalogItemPublic[]): AccessCatalogItemPublic[] {
  return items.filter((item) => item.retiredAt === null && item.application === null);
}

function selectedNodeIds(filters: LevelFilters): string[] {
  return Object.values(filters).filter((id) => id !== '');
}

/** A row matches when every picked entry is on its path. */
export function rowMatches(item: AccessCatalogItemPublic, filters: LevelFilters): boolean {
  return selectedNodeIds(filters).every((id) => item.path.some((step) => step.id === id));
}

/**
 * A level's filter options: its live entries that sit under every entry picked at a
 * shallower level — so the filters narrow each other instead of offering dead ends.
 */
export function levelOptions(
  level: AccessLevelPublic,
  levels: readonly AccessLevelPublic[],
  nodes: readonly AccessNodePublic[],
  filters: LevelFilters,
): AccessNodePublic[] {
  const above = levels
    .filter((candidate) => candidate.depth < level.depth)
    .map((candidate) => filters[candidate.id] ?? '')
    .filter((id) => id !== '');
  return nodes
    .filter(
      (node) =>
        node.retiredAt === null &&
        node.level.id === level.id &&
        above.every((id) => node.path.some((step) => step.id === id)),
    )
    .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));
}

/** Picking at one level clears the deeper picks, which may no longer be under it. */
export function withLevelPick(
  levels: readonly AccessLevelPublic[],
  filters: LevelFilters,
  levelId: string,
  nodeId: string,
): LevelFilters {
  const depth = levels.find((level) => level.id === levelId)?.depth ?? 0;
  const next: Record<string, string> = {};
  for (const level of levels) {
    if (level.id === levelId) next[level.id] = nodeId;
    else if (level.depth < depth) next[level.id] = filters[level.id] ?? '';
    else next[level.id] = '';
  }
  return next;
}

export type RecordIndex = ReadonlyMap<string, AccessRecordPublic>;

const cellKey = (userId: string, itemId: string): string => `${userId}|${itemId}`;

/** One live record per person × row — the server lists live ones only by default. */
export function indexRecords(records: readonly AccessRecordPublic[]): RecordIndex {
  const index = new Map<string, AccessRecordPublic>();
  for (const record of records) {
    const key = cellKey(record.user.id, record.catalogItem.id);
    if (!index.has(key)) index.set(key, record);
  }
  return index;
}

export function recordAt(
  index: RecordIndex,
  userId: string,
  itemId: string,
): AccessRecordPublic | undefined {
  return index.get(cellKey(userId, itemId));
}

export function matrixPeople(
  users: readonly UserPublic[],
  filters: MatrixFilters,
  rows: readonly AccessCatalogItemPublic[],
  index: RecordIndex,
): UserPublic[] {
  const needle = filters.search.trim().toLowerCase();
  return users
    .filter((user) => filters.people === 'all' || user.active)
    .filter(
      (user) =>
        needle === '' ||
        user.name.toLowerCase().includes(needle) ||
        user.email.toLowerCase().includes(needle),
    )
    .filter(
      (user) =>
        filters.show === 'all' ||
        rows.some((item) => {
          const record = recordAt(index, user.id, item.id);
          return record !== undefined && record.status !== 'active';
        }),
    )
    .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));
}

/* ------------------------------- heat grid -------------------------------- */

/**
 * The heat grid (round-2 demo A3, "Heat grid and side list"): the demo's companies against
 * channels, read through the admin's hierarchy — a grid row per top-level group (the same
 * groups the other two views draw), a grid column per entry one level below it. A catalog
 * row filed at the top level, or not filed, is its own column. One square holds every
 * catalog row filed under that pair, so every record the other views show lands in exactly
 * one square.
 */
export function heatColumn(item: AccessCatalogItemPublic): string {
  return item.path[1]?.name ?? item.name;
}

/**
 * The person has the access today: active, or still held while its removal is pending.
 * Needs access and Requested are awaiting a grant, and Removed is gone — none of those count.
 */
const HAS_ACCESS: ReadonlySet<AccessRecordStatus> = new Set<AccessRecordStatus>(['active', 'remove_access']);

/** IT has work open — every status that needs someone to act: the demo's yellow dot. */
const OPEN_WORK: ReadonlySet<AccessRecordStatus> = new Set<AccessRecordStatus>([
  'needs_access',
  'requested',
  'remove_access',
]);

export function hasAccess(record: AccessRecordPublic): boolean {
  return HAS_ACCESS.has(record.status);
}

export function isOpenWork(record: AccessRecordPublic): boolean {
  return OPEN_WORK.has(record.status);
}

export interface HeatSquare {
  key: string;
  group: string;
  column: string;
  items: AccessCatalogItemPublic[];
  /** The live records of the people in view, on this square's catalog rows. */
  records: AccessRecordPublic[];
  /** People who have it (`hasAccess`; pending and removed do not count): the square's number. */
  people: number;
  open: number;
  /** Something here is not active — what "Only what needs work" keeps in front. */
  attention: boolean;
}

export interface HeatGrid {
  groups: string[];
  columns: string[];
  squares: ReadonlyMap<string, HeatSquare>;
  /** The fullest square's number (at least 1), for the shade scale. */
  max: number;
}

export const heatKey = (group: string, column: string): string => `${group}\u0000${column}`;

export function heatGrid(
  groups: readonly (readonly [string, readonly AccessCatalogItemPublic[]])[],
  people: readonly UserPublic[],
  index: RecordIndex,
): HeatGrid {
  const squares = new Map<string, HeatSquare>();
  const columns = new Set<string>();
  for (const [group, items] of groups) {
    for (const item of items) {
      const column = heatColumn(item);
      columns.add(column);
      const key = heatKey(group, column);
      let square = squares.get(key);
      if (square === undefined) {
        square = { key, group, column, items: [], records: [], people: 0, open: 0, attention: false };
        squares.set(key, square);
      }
      square.items.push(item);
      for (const person of people) {
        const record = recordAt(index, person.id, item.id);
        if (record !== undefined) square.records.push(record);
      }
    }
  }
  for (const square of squares.values()) {
    square.people = new Set(
      square.records.filter(hasAccess).map((record) => record.user.id),
    ).size;
    square.open = square.records.filter(isOpenWork).length;
    square.attention = square.records.some((record) => record.status !== 'active');
  }
  return {
    groups: groups.map(([group]) => group),
    columns: [...columns].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' })),
    squares,
    max: Math.max(1, ...[...squares.values()].map((square) => square.people)),
  };
}

/** One line of a picked square's side list: a person on one of its catalog rows. */
export interface HeatEntry {
  person: UserPublic;
  item: AccessCatalogItemPublic;
  /** Undefined is an empty cell, the same as in Row and Table view. */
  record: AccessRecordPublic | undefined;
}

/**
 * A picked square's side list: every person in view on each of its catalog rows — the same
 * cells Row and Table view draw, empty ones included. Open work first, then active, then
 * empty; by name within each.
 */
export function squareEntries(
  square: HeatSquare,
  people: readonly UserPublic[],
  index: RecordIndex,
): HeatEntry[] {
  const rank = (record: AccessRecordPublic | undefined): number =>
    record === undefined ? 2 : record.status === 'active' ? 1 : 0;
  return people
    .flatMap((person) =>
      square.items.map((item) => ({ person, item, record: recordAt(index, person.id, item.id) })),
    )
    .sort(
      (a, b) =>
        rank(a.record) - rank(b.record) ||
        a.person.name.localeCompare(b.person.name, undefined, { sensitivity: 'base' }),
    );
}

/**
 * A square's fill, as a share of the accent mixed into the surface: 6% when empty, up to
 * 45% for the fullest — capped (as in the demo) so the number keeps body-text contrast in
 * every palette, light and dark. "Only what needs work" steps a square with nothing open
 * back to the floor.
 */
export function heatShade(people: number, max: number, quiet: boolean): number {
  return quiet ? 6 : Math.round(6 + (people / max) * 39);
}
